// AppointmentsView — "Citas". A standalone module to schedule a meeting with a
// client about a specific policy type (Lissette 2026-06-29). The form shows
// extra fields that depend on the chosen policy type, and on save a copy is
// emailed to the agency email (pre-filled, editable to any other address).
//
// Backend: /api/v1/appointments (see appointments.routes.ts). Table view with a
// "Nueva cita" modal; delete is gated by the agency's seat delete permission.

import { useState, useMemo, useEffect } from 'react';
import { CalendarPlus, Plus, Trash2, Pencil, X, Mail, Phone, Video, MapPin } from 'lucide-react';
import { Modal, ClientSearchSelect, confirmDialog, NoteEditor } from './ui.jsx';
import { POLICY_TYPES, getPolicyType } from '../policyTypes.js';
import { lineaDelRamo, nombreDeLinea } from '../lineasDeBloqueo.js';
import { canDelete } from '../seatPermissions.js';
import { toast } from './Toast.jsx';
import {
  createCita as apiCreate,
  updateCita as apiUpdate,
  deleteCita as apiDelete,
} from '../api.js';

const STATUS = [
  { id: 'scheduled', es: 'Agendada', en: 'Scheduled', color: '#00d4ff' },
  { id: 'confirmed', es: 'Confirmada', en: 'Confirmed', color: '#00ff88' },
  { id: 'completed', es: 'Completada', en: 'Completed', color: '#64748b' },
  { id: 'cancelled', es: 'Cancelada', en: 'Cancelled', color: '#ff3d6b' },
];
const stMeta = (id) => STATUS.find((s) => s.id === id) || STATUS[0];
const MODES = [
  { id: 'in_person', es: 'En persona', en: 'In person', Icon: MapPin },
  { id: 'phone', es: 'Llamada', en: 'Phone', Icon: Phone },
  { id: 'video', es: 'Video', en: 'Video', Icon: Video },
];

// Policy-type-dependent extra fields. Keyed by policy-type id; each entry is the
// list of extra fields to ask. Anything not listed falls back to DEFAULT_FIELDS.
const TYPE_FIELDS = {
  aca:    [{ es: 'Ingreso anual estimado', en: 'Estimated annual income' }, { es: 'Personas en el hogar', en: 'Household size' }, { es: 'Plan actual', en: 'Current plan' }],
  ma:     [{ es: 'Medicare ID / MBI', en: 'Medicare ID / MBI' }, { es: 'Médico de cabecera', en: 'Primary doctor' }, { es: 'Medicamentos', en: 'Medications' }],
  medsup: [{ es: 'Medicare ID / MBI', en: 'Medicare ID / MBI' }, { es: 'Plan actual', en: 'Current plan' }],
  partd:  [{ es: 'Medicare ID / MBI', en: 'Medicare ID / MBI' }, { es: 'Medicamentos', en: 'Medications' }],
  life:   [{ es: 'Monto de cobertura', en: 'Coverage amount' }, { es: 'Beneficiario', en: 'Beneficiary' }, { es: 'Condiciones de salud', en: 'Health conditions' }],
  final:  [{ es: 'Monto de cobertura', en: 'Coverage amount' }, { es: 'Beneficiario', en: 'Beneficiary' }],
  home:   [{ es: 'Dirección de la propiedad', en: 'Property address' }, { es: 'Año de construcción', en: 'Year built' }],
  auto:   [{ es: 'Vehículo (año/marca/modelo)', en: 'Vehicle (year/make/model)' }, { es: 'VIN', en: 'VIN' }, { es: 'Licencia de conducir', en: "Driver's license" }],
  renters:[{ es: 'Dirección de la propiedad', en: 'Property address' }],
  commercial: [{ es: 'Nombre del negocio', en: 'Business name' }, { es: 'Tipo de negocio', en: 'Business type' }],
  umbrella: [{ es: 'Pólizas subyacentes', en: 'Underlying policies' }],
  pet:    [{ es: 'Mascota (tipo/raza)', en: 'Pet (type/breed)' }],
  travel: [{ es: 'Destino', en: 'Destination' }, { es: 'Fechas del viaje', en: 'Travel dates' }],
};
const DEFAULT_FIELDS = [{ es: 'Plan de interés', en: 'Plan of interest' }];
// Built-in per-type fields + the agency's own saved custom labels for that type
// (customDefs = { [typeId]: ["Label", …] }). Custom labels that duplicate a
// built-in (case-insensitive) are dropped so React keys stay unique.
const fieldsFor = (typeId, customDefs) => {
  const base = (typeId && TYPE_FIELDS[typeId]) || DEFAULT_FIELDS;
  const seen = new Set(base.flatMap((f) => [String(f.es || '').toLowerCase(), String(f.en || '').toLowerCase()]));
  const custom = (typeId && Array.isArray(customDefs?.[typeId]) ? customDefs[typeId] : [])
    .filter((l) => l && !seen.has(String(l).toLowerCase()))
    .map((l) => ({ es: l, en: l, custom: true }));
  return [...base, ...custom];
};

const fmtWhen = (iso, es) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(es ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
// <input type="datetime-local"> wants 'YYYY-MM-DDTHH:mm' in LOCAL time.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const nameOf = (c) => (c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '');

export function AppointmentsView({
  appointments = [], onAppointmentsChange,
  clients = [], currentUser, lang, agencyEmail = '', canEdit = true,
  customFieldDefs = {}, onCustomFieldDefsChange,
}) {
  const es = lang === 'es';
  const allowDelete = canEdit && canDelete(currentUser);
  const [statusF, setStatusF] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const blank = () => ({
    clientId: '', clientName: '', clientEmail: '', clientPhone: '',
    policyType: '', scheduledAt: '', mode: 'in_person', location: '',
    status: 'scheduled', notes: '', notifyEmail: agencyEmail || currentUser?.email || '',
    formData: {}, avisarAlCliente: true,
  });
  const [form, setForm] = useState(blank);

  const tLabel = (id) => { const p = getPolicyType ? getPolicyType(id) : null; return p ? (es ? (p.labelEs || p.label) : p.label) : (id || '—'); };

  const filtered = useMemo(() => {
    let rows = appointments || [];
    if (statusF !== 'all') rows = rows.filter((a) => a.status === statusF);
    const term = q.trim().toLowerCase();
    if (term) rows = rows.filter((a) => [a.clientName, a.clientEmail, a.policyType, a.location, a.notes].filter(Boolean).join(' ').toLowerCase().includes(term));
    return rows.slice().sort((a, b) => String(b.scheduledAt || '').localeCompare(String(a.scheduledAt || '')));
  }, [appointments, statusF, q]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      clientId: a.clientId || '', clientName: a.clientName || '', clientEmail: a.clientEmail || '', clientPhone: a.clientPhone || '',
      policyType: a.policyType || '', scheduledAt: toLocalInput(a.scheduledAt), mode: a.mode || 'in_person', location: a.location || '',
      status: a.status || 'scheduled', notes: a.notes || '', notifyEmail: a.notifyEmail || agencyEmail || currentUser?.email || '',
      formData: { ...(a.formData || {}) }, avisarAlCliente: true,
    });
    setOpen(true);
  };
  const onPickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    setForm((f) => ({ ...f, clientId: cid, clientName: c ? nameOf(c) : f.clientName, clientEmail: c?.email || f.clientEmail, clientPhone: c?.phone || f.clientPhone }));
  };

  // Lo que pasó con el correo al cliente (fecha, hora y lugar, sin PDF). Si no
  // salió, se dice por qué: un correo que no sale en silencio es justo lo que
  // hacía que el cliente no se enterara (Lissette 2026-10-29).
  const avisarDelCorreoAlCliente = (estado, esCambio) => {
    if (estado === 'enviado') {
      toast.success(esCambio
        ? (es ? 'Al cliente le llegó un correo con el nuevo horario ✓' : 'The client was emailed the new time ✓')
        : (es ? 'Al cliente le llegó un correo con la fecha, hora y lugar ✓' : 'The client was emailed the date, time and place ✓'));
    } else if (estado === 'sin_correo') {
      toast.error(es ? 'Al cliente no se le envió correo: la cita no tiene su correo.' : 'No email sent to the client: the appointment has no client email.');
    } else if (estado === 'sin_buzon') {
      toast.error(es ? 'Al cliente no se le envió correo: conecta tu correo en Integraciones.' : 'No email sent to the client: connect your mailbox in Integrations.');
    } else if (estado === 'dado_de_baja') {
      toast.error(es ? 'Al cliente no se le envió correo: se dio de baja de los correos.' : 'No email sent to the client: they unsubscribed.');
    } else if (estado === 'fallo') {
      toast.error(es ? 'No se pudo enviar el correo al cliente.' : 'The email to the client could not be sent.');
    } else if (estado === 'en_camino') {
      // El buzón tardó más de 15 s: la cita ya está guardada y el correo sigue
      // saliendo por detrás (E-411). Se dice tal cual, sin dar por hecho nada.
      toast.info(es ? 'El correo al cliente se está enviando; tu buzón tardó en contestar.' : 'The email to the client is on its way; your mailbox was slow to answer.');
    }
  };

  const save = async () => {
    if (!form.scheduledAt) { toast.error(es ? 'Elige fecha y hora' : 'Pick a date and time'); return; }
    const payload = {
      clientId: form.clientId || '', clientName: form.clientName || '',
      clientEmail: form.clientEmail || '', clientPhone: form.clientPhone || '',
      policyType: form.policyType || '', scheduledAt: new Date(form.scheduledAt).toISOString(),
      mode: form.mode || '', location: form.location || '', status: form.status || 'scheduled',
      notes: form.notes || '', notifyEmail: form.notifyEmail || '', formData: form.formData || {},
      avisarAlCliente: form.avisarAlCliente !== false,
    };
    setBusy(true);
    try {
      if (editing) {
        const { correoAlCliente, ...updated } = await apiUpdate(editing.id, payload);
        onAppointmentsChange((prev) => prev.map((a) => (a.id === editing.id ? updated : a)));
        toast.success(es ? 'Cita actualizada ✓' : 'Appointment updated ✓');
        avisarDelCorreoAlCliente(correoAlCliente, true);
      } else {
        const { correoAlCliente, ...created } = await apiCreate(payload);
        onAppointmentsChange((prev) => [created, ...prev]);
        toast.success(es ? 'Cita creada ✓ — la copia te llega por correo en un momento' : 'Appointment created ✓ — the copy is on its way to your email');
        avisarDelCorreoAlCliente(correoAlCliente, false);
      }
      setOpen(false);
    } catch (e) { toast.error((es ? 'No se pudo guardar: ' : 'Failed to save: ') + e.message); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    const ok = await confirmDialog({ title: es ? 'Eliminar cita' : 'Delete appointment', message: es ? `¿Estás seguro de borrar la cita de ${a.clientName || 'este cliente'}? Esta acción no se puede deshacer.` : `Delete the appointment for ${a.clientName || 'this client'}? This cannot be undone.`, danger: true, confirmLabel: es ? 'Eliminar' : 'Delete' });
    if (!ok) return;
    try { await apiDelete(a.id); onAppointmentsChange((prev) => prev.filter((x) => x.id !== a.id)); }
    catch (e) { toast.error((es ? 'No se pudo eliminar: ' : 'Failed to delete: ') + e.message); }
  };

  const extraFields = fieldsFor(form.policyType, customFieldDefs);
  const th = { textAlign: 'left', padding: '.45rem .6rem', fontSize: '.62rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const td = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#1a3550)', verticalAlign: 'middle' };
  const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <div className="dkt-sec-title">📅 {es ? 'Citas' : 'Appointments'}</div>
          <div className="dkt-sec-sub">{es ? 'Agenda citas con tus clientes por tipo de póliza' : 'Schedule client appointments by policy type'}</div>
        </div>
        {canEdit && <button type="button" className="dkt-btn primary" onClick={openNew}><Plus size={15} /> {es ? 'Nueva cita' : 'New appointment'}</button>}
      </div>

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="dkt-inp" style={{ width: 'auto', padding: '.38rem .6rem', fontSize: '.75rem' }} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">{es ? 'Todas' : 'All'}</option>
          {STATUS.map((s) => <option key={s.id} value={s.id}>{es ? s.es : s.en}</option>)}
        </select>
        <input className="dkt-search" style={{ flex: 1, minWidth: 160, maxWidth: 320 }} placeholder={es ? '🔍 Buscar…' : '🔍 Search…'} value={q} onChange={(e) => setQ(e.target.value)} />
        <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginLeft: 'auto' }} className="dkt-mono">{filtered.length}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="dkt-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
          {es ? 'Sin citas. Crea una con “Nueva cita”.' : 'No appointments yet.'}
        </div>
      ) : (
        <div className="dkt-card" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={th}>{es ? 'Cliente' : 'Client'}</th>
              <th style={th}>{es ? 'Tipo de póliza' : 'Policy type'}</th>
              <th style={th}>{es ? 'Fecha y hora' : 'Date & time'}</th>
              <th style={th}>{es ? 'Modalidad' : 'Mode'}</th>
              <th style={th}>{es ? 'Estado' : 'Status'}</th>
              <th style={th}>{es ? 'Copia a' : 'Copy to'}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {filtered.map((a) => {
                const m = MODES.find((x) => x.id === a.mode);
                const sm = stMeta(a.status);
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--c-border-soft,rgba(26,58,94,.5))' }}>
                    <td style={{ ...td, color: 'var(--c-white,#eef6ff)', fontWeight: 600 }}>{a.clientName || '—'}{a.clientEmail && <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontWeight: 400 }}>{a.clientEmail}</div>}</td>
                    <td style={td}>{a.policyType ? <span>{(getPolicyType(a.policyType)?.icon) || ''} {tLabel(a.policyType)}</span> : '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtWhen(a.scheduledAt, es)}</td>
                    <td style={td}>{m ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><m.Icon size={12} /> {es ? m.es : m.en}</span> : '—'}</td>
                    <td style={td}><span className="dkt-bdg" style={{ background: `${sm.color}22`, color: sm.color, border: `1px solid ${sm.color}55` }}>{es ? sm.es : sm.en}</span></td>
                    <td style={{ ...td, fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)' }}>{a.notifyEmail || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canEdit && <button type="button" title={es ? 'Editar' : 'Edit'} onClick={() => openEdit(a)} style={iconBtn}><Pencil size={13} /></button>}
                      {allowDelete && <button type="button" title={es ? 'Eliminar' : 'Delete'} onClick={() => remove(a)} style={{ ...iconBtn, marginLeft: 4 }}><Trash2 size={13} /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? (es ? 'Editar cita' : 'Edit appointment') : (es ? 'Nueva cita' : 'New appointment')} onClose={() => setOpen(false)} wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
            <div>
              <label className="dkt-input-lbl">{es ? 'Cliente' : 'Client'}</label>
              <ClientSearchSelect clients={clients} value={form.clientId} onChange={(e) => onPickClient(e.target.value)} lang={lang} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Nombre (si no es cliente)' : 'Name (if not a client)'}</label><input className="dkt-inp" value={form.clientName} onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))} /></div>
              <div><label className="dkt-input-lbl">{es ? 'Tipo de póliza' : 'Policy type'}</label>
                <select className="dkt-inp" value={form.policyType} onChange={(e) => setForm((f) => ({ ...f, policyType: e.target.value }))}>
                  <option value="">{es ? '— Elige —' : '— Choose —'}</option>
                  {POLICY_TYPES.map((p) => <option key={p.id} value={p.id}>{es ? (p.labelEs || p.label) : p.label}</option>)}
                </select>
                {/* Qué hora cierra esta cita en el calendario y en el enlace de
                    agendar (Lissette 2026-10-11): solo la de su línea. */}
                {form.policyType && (
                  <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
                    🔒 {lineaDelRamo(form.policyType)
                      ? (es ? `Bloquea esa hora solo para ${nombreDeLinea(lineaDelRamo(form.policyType), true)}` : `Blocks that time only for ${nombreDeLinea(lineaDelRamo(form.policyType), false)}`)
                      : (es ? 'Bloquea esa hora para todas las líneas' : 'Blocks that time for every line')}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Correo del cliente' : 'Client email'}</label><input className="dkt-inp" type="email" value={form.clientEmail} onChange={(e) => setForm((f) => ({ ...f, clientEmail: e.target.value }))} />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '.35rem', marginTop: '.35rem', fontSize: '.68rem', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.avisarAlCliente !== false} onChange={(e) => setForm((f) => ({ ...f, avisarAlCliente: e.target.checked }))} />
                  <span>{editing
                    ? (es ? 'Si cambias la hora, enviarle al cliente un correo con el nuevo horario' : 'If you change the time, email the client the new time')
                    : (es ? 'Enviarle al cliente un correo con la fecha, hora y lugar (sin el PDF)' : 'Email the client the date, time and place (no PDF)')}</span>
                </label>
              </div>
              <div><label className="dkt-input-lbl">{es ? 'Teléfono' : 'Phone'}</label><input className="dkt-inp" value={form.clientPhone} onChange={(e) => setForm((f) => ({ ...f, clientPhone: e.target.value }))} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Fecha y hora' : 'Date & time'}</label><input className="dkt-inp" type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))} /></div>
              <div><label className="dkt-input-lbl">{es ? 'Modalidad' : 'Mode'}</label>
                <select className="dkt-inp" value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}>
                  {MODES.map((m) => <option key={m.id} value={m.id}>{es ? m.es : m.en}</option>)}
                </select>
              </div>
            </div>
            <div><label className="dkt-input-lbl">{es ? 'Lugar / enlace' : 'Location / link'}</label><input className="dkt-inp" placeholder={es ? 'Oficina, dirección o link de video' : 'Office, address or video link'} value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></div>

            {/* Policy-type-dependent fields */}
            {extraFields.length > 0 && (
              <div>
                <div className="dkt-cyan-lbl" style={{ marginBottom: '.4rem' }}>{es ? 'Datos según el tipo de póliza' : 'Policy-type details'}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                  {extraFields.map((fld) => {
                    const key = es ? fld.es : fld.en;
                    return (
                      <div key={key}>
                        <label className="dkt-input-lbl">{key}</label>
                        <input className="dkt-inp" value={form.formData[key] || ''} onChange={(e) => setForm((f) => ({ ...f, formData: { ...f.formData, [key]: e.target.value } }))} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Editor de campos personalizados por tipo (Lissette 2026-07-01):
                defines los campos una vez para este tipo y reaparecen en cada
                cita del mismo tipo. Se guardan en la agencia. */}
            {form.policyType && onCustomFieldDefsChange && (
              <ApptCustomFieldEditor typeId={form.policyType} defs={customFieldDefs} onChange={onCustomFieldDefsChange} es={es} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Estado' : 'Status'}</label>
                <select className="dkt-inp" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  {STATUS.map((s) => <option key={s.id} value={s.id}>{es ? s.es : s.en}</option>)}
                </select>
              </div>
              <div><label className="dkt-input-lbl"><Mail size={11} style={{ verticalAlign: 'middle' }} /> {es ? 'Enviar copia a' : 'Email copy to'}</label><input className="dkt-inp" type="email" value={form.notifyEmail} onChange={(e) => setForm((f) => ({ ...f, notifyEmail: e.target.value }))} placeholder={es ? 'Correo de la agencia u otro' : 'Agency email or another'} /></div>
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Al crear la cita se envía una copia a este correo (por defecto el de la agencia; puedes cambiarlo).' : 'A copy is emailed to this address when the appointment is created (defaults to the agency email; you can change it).'}</div>

            <div><label className="dkt-input-lbl">{es ? 'Notas' : 'Notes'}</label><NoteEditor lang={es ? 'es' : 'en'} rows={3} value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} /></div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
              <button type="button" className="dkt-btn ghost" onClick={() => setOpen(false)}>{es ? 'Cancelar' : 'Cancel'}</button>
              <button type="button" className="dkt-btn primary" disabled={busy} onClick={save}>{busy ? (es ? 'Guardando…' : 'Saving…') : (editing ? (es ? 'Guardar' : 'Save') : (es ? 'Crear cita' : 'Create appointment'))}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Inline editor for the agency's saved custom appointment fields, scoped to the
// currently-selected policy type. Adding/removing a label persists immediately
// via onChange (parent saves to AgencySetting.appointmentFieldDefs). The labels
// then render as extra inputs on every appointment of this type and flow into
// the confirmation email.
function ApptCustomFieldEditor({ typeId, defs, onChange, es }) {
  const [label, setLabel] = useState('');
  const current = Array.isArray(defs?.[typeId]) ? defs[typeId] : [];
  const add = () => {
    const l = label.trim();
    if (!l) return;
    if (current.some((x) => String(x).toLowerCase() === l.toLowerCase())) { setLabel(''); return; }
    onChange({ ...defs, [typeId]: [...current, l] });
    setLabel('');
  };
  const remove = (l) => { onChange({ ...defs, [typeId]: current.filter((x) => x !== l) }); };
  return (
    <div style={{ borderTop: '1px dashed var(--c-border,#1a3550)', paddingTop: '.6rem' }}>
      <div className="dkt-cyan-lbl" style={{ marginBottom: '.4rem' }}>
        {es ? 'Campos personalizados de este tipo (se guardan y reaparecen)' : 'Custom fields for this type (saved & reused)'}
      </div>
      {current.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', marginBottom: '.5rem' }}>
          {current.map((l) => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '.25rem .55rem', borderRadius: 100, background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.72rem', color: 'var(--c-text,#c8dff0)' }}>
              {l}
              <button type="button" onClick={() => remove(l)} title={es ? 'Quitar' : 'Remove'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 0, display: 'flex' }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '.4rem' }}>
        <input className="dkt-inp" style={{ flex: 1 }} value={label}
          placeholder={es ? 'Nombre del campo (ej. Número de grupo)' : 'Field name (e.g. Group number)'}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" className="dkt-btn ghost xs" onClick={add}><Plus size={13} />{es ? 'Agregar' : 'Add'}</button>
      </div>
    </div>
  );
}
